import { useState } from "react";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { Card } from "../components/Card.js";
import { Toggle, Btn } from "../components/index.js";
import { EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { auth, getClaims } from "../lib/api.js";

const SECTIONS = [
  "組織・一般",
  "認証・パスワード",
  "プロビジョニング (SCIM)",
  "API / 開発者",
  "Webhook",
  "通知連携",
  "エクスポート",
  "データ保持・PII",
  "アクセシビリティ",
];

export function Settings() {
  const [sec, setSec] = useState(SECTIONS[0]);
  const [gate, setGate] = useState(true);
  const [twoStep, setTwoStep] = useState(false);
  const [hires, setHires] = useState(() => {
    try {
      return localStorage.getItem("tebiki.capture.hires") === "1";
    } catch {
      return false;
    }
  });
  function onHires(v: boolean) {
    setHires(v);
    try {
      localStorage.setItem("tebiki.capture.hires", v ? "1" : "0");
    } catch {
      /* storage unavailable — preference simply won't persist */
    }
  }

  return (
    <ConsoleShell
      breadcrumb={["TEBIKI", "設定"]}
      actions={
        <Btn variant="primary" disabled title="設定の保存は準備中です">
          変更を保存
        </Btn>
      }
    >
      <PageHead title={<>組織<span className="hl">設定</span></>} />
      <div className="setlayout" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 30 }}>
        <nav className="setnav reveal" aria-label="設定セクション">
          {SECTIONS.map((s) => (
            <a
              key={s}
              href="#"
              className={s === sec ? "on" : undefined}
              aria-current={s === sec ? "true" : undefined}
              onClick={(e) => {
                e.preventDefault();
                setSec(s);
              }}
              style={{ display: "block", padding: "9px 12px", borderRadius: 9, color: s === sec ? "var(--ink)" : "var(--muted)", background: s === sec ? "var(--surface2)" : undefined, fontWeight: 600, fontSize: 14 }}
            >
              {s}
            </a>
          ))}
        </nav>
        <div className="reveal d1">
          {sec === "組織・一般" ? (
          <>
          <Card crop pad style={{ marginBottom: 18 }}>
            <div className="kicker" style={{ marginBottom: 8 }}>
              組織・一般
            </div>
            <FormRow label="組織名" hint="請求書・共有URLに表示">
              <input className="input jp" defaultValue="サンプル商事株式会社" />
            </FormRow>
            <FormRow label="既定の言語" hint="新規マニュアルの原文言語">
              <select className="select">
                <option>日本語</option>
                <option>English</option>
              </select>
            </FormRow>
            <FormRow label="業種" hint="AIレビューの業種別チェックに使用">
              <select className="select">
                <option>飲食・小売</option>
                <option>製造</option>
                <option>医療・介護</option>
              </select>
            </FormRow>
            <FormRow label="タイムゾーン">
              <select className="select">
                <option>Asia/Tokyo (GMT+9)</option>
              </select>
            </FormRow>
          </Card>
          <Card crop pad>
            <div className="kicker" style={{ marginBottom: 8 }}>
              既定の動作
            </div>
            <FormRow label="公開前にAI品質ゲート" hint="抜け・安全リスクを自動検査">
              <div className="row">
                <Toggle checked={gate} onChange={setGate} label="AI品質ゲート" />
                <span className="jp" style={{ color: "var(--muted)" }}>
                  {gate ? "有効" : "無効"}
                </span>
              </div>
            </FormRow>
            <FormRow label="多候補キーフレーム数" hint="動画フローで提示する候補数">
              <select className="select" style={{ maxWidth: 120 }}>
                <option>3（推奨）</option>
                <option>2</option>
                <option>5</option>
              </select>
            </FormRow>
            <FormRow label="高精細撮影" hint="極小ラベル向け。容量と通信量が増えます（既定=標準）">
              <div className="row">
                <Toggle checked={hires} onChange={onHires} label="高精細撮影" />
                <span className="jp" style={{ color: "var(--muted)" }}>
                  {hires ? "高精細（5Mbps）" : "標準（4Mbps）"}
                </span>
              </div>
            </FormRow>
            <FormRow label="2段階承認" hint="公開に複数承認者を必須化">
              <div className="row">
                <Toggle checked={twoStep} onChange={setTwoStep} label="2段階承認" />
                <span className="jp" style={{ color: "var(--muted)" }}>
                  {twoStep ? "有効" : "無効"}
                </span>
              </div>
            </FormRow>
          </Card>
          </>
          ) : sec === "認証・パスワード" ? (
            <PasswordCard />
          ) : (
            <EmptyState icon={<Icon name="settings" size={30} />} title={`「${sec}」は準備中です`}>
              この設定セクションは準備中です。現在は「組織・一般」「認証・パスワード」の設定のみご利用いただけます。
            </EmptyState>
          )}
        </div>
      </div>
    </ConsoleShell>
  );
}

/** Self-service password change for the signed-in user. */
function PasswordCard() {
  const claims = getClaims();
  // Store-PIN sessions (sub = "store:…") aren't a personal user — they can't set a personal password.
  const isStoreSession = (claims?.sub ?? "").startsWith("store:");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (next.length < 8) {
      setMsg({ kind: "err", text: "新しいパスワードは8文字以上にしてください。" });
      return;
    }
    if (next !== confirm) {
      setMsg({ kind: "err", text: "確認用パスワードが一致しません。" });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await auth.changePassword(next, current || undefined);
      setCurrent("");
      setNext("");
      setConfirm("");
      setMsg({ kind: "ok", text: "パスワードを変更しました。次回から新しいパスワードでログインできます。" });
    } catch {
      setMsg({ kind: "err", text: "変更に失敗しました。現在のパスワードが正しいか、通信環境を確認してください。" });
    } finally {
      setBusy(false);
    }
  }

  if (isStoreSession) {
    return (
      <EmptyState icon={<Icon name="settings" size={30} />} title="店舗端末ログインではパスワードを設定できません">
        個人アカウント（メールアドレス）でログインすると、ここでパスワードを変更できます。店舗端末の入店は店舗コード＋PINです。
      </EmptyState>
    );
  }

  return (
    <Card crop pad style={{ maxWidth: 520 }}>
      <div className="kicker" style={{ marginBottom: 8 }}>ログインパスワード</div>
      <p className="jp" style={{ color: "var(--muted)", fontSize: 13, margin: "0 0 14px", lineHeight: 1.7 }}>
        メールアドレス＋パスワードでログインします。パスワードをまだ設定していない場合は「現在のパスワード」は空欄のままで設定できます。
      </p>
      <form onSubmit={submit}>
        <FormRow label="現在のパスワード" hint="未設定なら空欄でOK">
          <input className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </FormRow>
        <FormRow label="新しいパスワード" hint="8文字以上">
          <input className="input" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </FormRow>
        <FormRow label="新しいパスワード（確認）">
          <input className="input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </FormRow>
        {msg ? (
          <p className="jp" role={msg.kind === "err" ? "alert" : "status"} style={{ margin: "4px 0 12px", fontSize: 13, color: msg.kind === "err" ? "var(--danger)" : "var(--ok-ink)" }}>
            {msg.text}
          </p>
        ) : null}
        <Btn type="submit" variant="primary" disabled={busy}>
          {busy ? "変更中…" : "パスワードを変更"}
        </Btn>
      </form>
    </Card>
  );
}

function FormRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="formrow">
      <div className="lbl">
        <b>{label}</b>
        {hint ? <span>{hint}</span> : null}
      </div>
      <div>{children}</div>
    </div>
  );
}
